import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';

interface ElapsedTimeDisplayProps {
  startedAt: string | null | undefined;
  isPaused?: boolean;
  pausedDurationSeconds?: number;
}

const ElapsedTimeDisplay: React.FC<ElapsedTimeDisplayProps> = ({ startedAt, isPaused = false, pausedDurationSeconds = 0 }) => {
  const { t } = useLanguage();
  const [elapsedTime, setElapsedTime] = useState<string>('00:00:00');

  // Parse pausedDurationSeconds to handle string values from API
  const parsedPausedSeconds = typeof pausedDurationSeconds === 'string'
    ? parseFloat(pausedDurationSeconds) || 0
    : pausedDurationSeconds || 0;

  useEffect(() => {
    if (!startedAt) return;

    // If paused, freeze the current elapsed time and don't start interval
    if (isPaused) {
      return;
    }

    const sessionStartTime = new Date(startedAt);

    const updateElapsedTime = () => {
      const now = new Date();
      const totalDiff = now.getTime() - sessionStartTime.getTime();
      // Cap pausedMs to never exceed total elapsed time (handles data inconsistencies)
      const pausedMs = Math.min(parsedPausedSeconds * 1000, totalDiff);
      const activeDiff = Math.max(totalDiff - pausedMs, 0);

      const hours = Math.floor(activeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((activeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((activeDiff % (1000 * 60)) / 1000);

      setElapsedTime(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    updateElapsedTime();
    const interval = setInterval(updateElapsedTime, 1000);

    return () => clearInterval(interval);
  }, [startedAt, isPaused, parsedPausedSeconds]);

  return (
    <div className={`rounded-lg shadow px-4 flex items-center text-white ${
      isPaused
        ? 'bg-gradient-to-br from-yellow-500 to-yellow-600'
        : 'bg-gradient-to-br from-green-500 to-green-600'
    }`}>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4" />
        <div>
          <div className="text-xs font-semibold opacity-90">{t('operator.sessionTime')}</div>
          <div className="text-xl font-bold font-mono">{elapsedTime}</div>
        </div>
      </div>
    </div>
  );
};

export default ElapsedTimeDisplay;
